/**
 * WhatsApp Notification Service
 * Handles sending WhatsApp messages after job posting using webhook.whatapi.in
 */

// WhatsApp API Configuration
const WHATSAPP_CONFIG = {
  baseUrl: 'https://webhook.whatapi.in/webhook/691d6bfb1b9845c02d50aacb',
  defaultNumber: '917058869790', // Default recipient number
  timeout: 30000, // 30 seconds timeout
  retryAttempts: 3,
  retryDelay: 2000 // 2 seconds between retries
};

/**
 * Format job data into comma-separated message format
 * @param {Object} jobData - Job posting data
 * @param {Object} additionalData - Additional data like user info, package stats
 * @returns {string} Formatted message string
 */
const formatJobMessage = (jobData, additionalData = {}) => {
  try {
    console.log('📝 [WhatsApp] Formatting job message with data:', { jobData, additionalData });

    const {
      title = '',
      company = '',
      location = {},
      jobType = '',
      experienceLevel = '',
      workMode = '',
      description = ''
    } = jobData;

    const {
      employerName = 'Employer',
      remainingJobs = 0,
      totalJobs = 0,
      usedJobs = 0
    } = additionalData;

    // Format location string
    const locationStr = location.city && location.state
      ? `${location.city}, ${location.state}`
      : location.city || location.state || 'Not specified';

    // Create formatted message with proper escaping
    const messageData = {
      jobs: 1, // Always 1 for single job posting
      name: employerName,
      title: title || 'Job Position',
      company: company || 'Company',
      location: locationStr,
      jobtype: jobType || 'Full-time',
      experiencelevel: experienceLevel || 'Mid-level',
      workmode: workMode || 'Onsite',
      jobpostings: usedJobs + 1, // Current usage after this post
      remainingjob: Math.max(0, remainingJobs - 1), // Remaining after this post
      totalavailable: totalJobs || 0,
      text: description ? description.substring(0, 200).replace(/\n/g, ' ') : 'New job opportunity available',
      posted: new Date().toISOString().split('T')[0] // YYYY-MM-DD format
    };

    // Create comma-separated string
    const message = [
      messageData.jobs,
      messageData.name,
      messageData.title,
      messageData.company,
      messageData.location,
      messageData.jobtype,
      messageData.experiencelevel,
      messageData.workmode,
      messageData.jobpostings,
      messageData.remainingjob,
      messageData.totalavailable,
      messageData.text,
      messageData.posted
    ].join(',');

    console.log('✅ [WhatsApp] Message formatted successfully:', message.substring(0, 100) + '...');
    return message;
  } catch (error) {
    console.error('❌ [WhatsApp] Error formatting message:', error);
    // Return fallback message
    return `1,${additionalData.employerName || 'Employer'},Job Posted,${jobData.company || 'Company'},Location,Full-time,Mid-level,Onsite,1,0,1,New job posted successfully,${new Date().toISOString().split('T')[0]}`;
  }
};

/**
 * Send WhatsApp message with retry logic
 * @param {string} message - Formatted message string
 * @param {string} phoneNumber - Recipient phone number (optional)
 * @returns {Promise<Object>} Result object
 */
const sendWhatsAppMessage = async (message, phoneNumber = null) => {
  const targetNumber = phoneNumber || WHATSAPP_CONFIG.defaultNumber;
  let lastError = null;

  console.log(`📱 [WhatsApp] Attempting to send message to ${targetNumber}`);
  console.log(`📄 [WhatsApp] Message preview: ${message.substring(0, 150)}...`);

  for (let attempt = 1; attempt <= WHATSAPP_CONFIG.retryAttempts; attempt++) {
    try {
      console.log(`🔄 [WhatsApp] Attempt ${attempt}/${WHATSAPP_CONFIG.retryAttempts}`);

      // Encode message for URL
      const encodedMessage = encodeURIComponent(message);

      // Build the webhook URL
      const webhookUrl = `${WHATSAPP_CONFIG.baseUrl}?number=${targetNumber}&message=${encodedMessage}`;

      console.log(`🌐 [WhatsApp] Webhook URL: ${webhookUrl.substring(0, 100)}...`);

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), WHATSAPP_CONFIG.timeout);

      // Make the API call
      const response = await fetch(webhookUrl, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'UdaanJobs-WhatsApp-Service/1.0'
        }
      });

      clearTimeout(timeoutId);

      console.log(`📊 [WhatsApp] Response status: ${response.status}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Parse response
      const result = await response.json();
      console.log('📥 [WhatsApp] API Response:', result);

      if (result.accepted === true || result.success === true) {
        console.log('✅ [WhatsApp] Message sent successfully!');
        return {
          success: true,
          message: 'WhatsApp message sent successfully',
          data: result,
          attempt,
          timestamp: new Date().toISOString()
        };
      } else {
        throw new Error(`API rejected message: ${JSON.stringify(result)}`);
      }

    } catch (error) {
      lastError = error;
      console.error(`❌ [WhatsApp] Attempt ${attempt} failed:`, error.message);

      if (error.name === 'AbortError') {
        console.error(`⏰ [WhatsApp] Request timed out after ${WHATSAPP_CONFIG.timeout}ms`);
      }

      // If this isn't the last attempt, wait before retrying
      if (attempt < WHATSAPP_CONFIG.retryAttempts) {
        console.log(`⏳ [WhatsApp] Waiting ${WHATSAPP_CONFIG.retryDelay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, WHATSAPP_CONFIG.retryDelay));
      }
    }
  }

  // All attempts failed
  console.error(`💥 [WhatsApp] All ${WHATSAPP_CONFIG.retryAttempts} attempts failed. Last error:`, lastError?.message);
  return {
    success: false,
    error: lastError?.message || 'Unknown error occurred',
    attempts: WHATSAPP_CONFIG.retryAttempts,
    timestamp: new Date().toISOString()
  };
};

/**
 * Send job posting notification via WhatsApp
 * @param {Object} jobData - Job posting data
 * @param {Object} userInfo - User information
 * @param {Object} packageInfo - Package/subscription information
 * @returns {Promise<Object>} Result object
 */
export const sendJobPostingNotification = async (jobData, userInfo = {}, packageInfo = {}) => {
  try {
    console.log('🚀 [WhatsApp] Starting job posting notification process');
    console.log('📋 [WhatsApp] Job data:', { title: jobData.title, company: jobData.company });
    console.log('👤 [WhatsApp] User info:', { name: userInfo.name, phone: userInfo.phone });
    console.log('📦 [WhatsApp] Package info:', packageInfo);

    // Validate required data
    if (!jobData || !jobData.title) {
      throw new Error('Job data is required with at least a title');
    }

    // Prepare additional data for message formatting
    const additionalData = {
      employerName: userInfo.name || userInfo.firstName || 'Employer',
      remainingJobs: packageInfo.remainingJobs || 0,
      totalJobs: packageInfo.totalJobs || 0,
      usedJobs: packageInfo.usedJobs || 0
    };

    // Format the message
    const formattedMessage = formatJobMessage(jobData, additionalData);

    // Determine phone number (user's phone or default)
    const phoneNumber = userInfo.phone || WHATSAPP_CONFIG.defaultNumber;

    // Send the message
    const result = await sendWhatsAppMessage(formattedMessage, phoneNumber);

    if (result.success) {
      console.log('🎉 [WhatsApp] Job posting notification sent successfully!');
    } else {
      console.error('💔 [WhatsApp] Failed to send job posting notification');
    }

    return result;

  } catch (error) {
    console.error('💥 [WhatsApp] Error in sendJobPostingNotification:', error);
    return {
      success: false,
      error: error.message,
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Send job application notification to job poster via WhatsApp
 * @param {Object} params - Application parameters
 * @param {string} params.phoneNumber - Job poster's phone number
 * @param {string} params.userName - Applicant's name
 * @param {string} params.userQualification - Applicant's qualification
 * @param {string} params.userExperience - Applicant's experience
 * @param {string} params.userLocation - Applicant's location
 * @param {string} params.jobTitle - Job title (optional for context)
 * @returns {Promise<Object>} Result object
 */
export const sendJobApplicationNotification = async ({
  phoneNumber,
  userName,
  userQualification,
  userExperience,
  userLocation,
  jobTitle = 'your job'
}) => {
  try {
    // Validate required parameters
    if (!phoneNumber) {
      throw new Error('Phone number is required');
    }

    if (!userName || !userQualification || !userExperience || !userLocation) {
      throw new Error('Missing required applicant information');
    }

    // Use the production webhook URL for job applications
    const APPLICATION_WEBHOOK_URL = 'https://webhook.whatapi.in/webhook/69490b1de60589cc86149b6b';

    // Build message: udan,name,text,date,link,address
    const message = `udan,${jobTitle},${userName},${userQualification},${userExperience},${userLocation}`;

    // Ensure phone number has 91 prefix
    let formattedPhone = phoneNumber;
    if (!phoneNumber.startsWith('91')) {
      formattedPhone = '91' + phoneNumber;
    }

    const encodedNumber = encodeURIComponent(formattedPhone);
    const encodedMessage = encodeURIComponent(message);

    // Construct full URL
    const webhookUrl = `${APPLICATION_WEBHOOK_URL}?number=${encodedNumber}&message=${encodedMessage}`;

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), WHATSAPP_CONFIG.timeout);

    // Make the API call
    const response = await fetch(webhookUrl, {
      method: 'GET',
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'User-Agent': 'UdaanJobs-WhatsApp-Service/1.0'
      }
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Parse response
    let result;
    try {
      const responseText = await response.text();
      result = JSON.parse(responseText);
    } catch (parseError) {
      result = { success: true, message: 'Response received but not JSON' };
    }

    return {
      success: true,
      message: 'WhatsApp application notification sent successfully',
      data: result,
      timestamp: new Date().toISOString(),
      sentTo: formattedPhone,
      applicant: userName,
      jobTitle: jobTitle
    };

  } catch (error) {
    // Don't fail the application process if WhatsApp fails
    return {
      success: false,
      error: error.message || 'Failed to send WhatsApp notification',
      errorType: error.name || 'Unknown',
      timestamp: new Date().toISOString(),
      phoneNumber: phoneNumber || 'not provided',
      applicant: userName || 'not provided'
    };
  }
};

/**
 * Send package purchase notification to user via WhatsApp
 * @param {Object} params - Purchase parameters
 * @param {string} params.phoneNumber - User's phone number
 * @param {string} params.userName - User's name
 * @param {string} params.packageName - Package name purchased
 * @param {string} params.purchaseDate - Date of purchase (optional)
 * @returns {Promise<Object>} Result object
 */
export const sendPackagePurchaseNotification = async ({
  phoneNumber,
  userName,
  packageName,
  purchaseDate
}) => {
  try {
    // Validate required parameters
    if (!phoneNumber) {
      throw new Error('Phone number is required');
    }

    if (!userName || !packageName) {
      throw new Error('Missing required information (userName or packageName)');
    }

    // Use the package purchase webhook URL
    const PACKAGE_WEBHOOK_URL = 'https://webhook.whatapi.in/webhook/694914fbe60589cc8614a851';

    // Format purchase date
    const formattedDate = purchaseDate || new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // Build message: udaan,name,text,date
    // name = userName
    // text = packageName
    // date = purchaseDate
    const message = `udaan,${userName},${packageName},${formattedDate}`;

    // Ensure phone number has 91 prefix
    let formattedPhone = phoneNumber;
    if (!phoneNumber.startsWith('91')) {
      formattedPhone = '91' + phoneNumber;
    }

    const encodedNumber = encodeURIComponent(formattedPhone);
    const encodedMessage = encodeURIComponent(message);

    // Construct full URL
    const webhookUrl = `${PACKAGE_WEBHOOK_URL}?number=${encodedNumber}&message=${encodedMessage}`;

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), WHATSAPP_CONFIG.timeout);

    // Make the API call
    const response = await fetch(webhookUrl, {
      method: 'GET',
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'User-Agent': 'UdaanJobs-WhatsApp-Service/1.0'
      }
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Parse response
    let result;
    try {
      const responseText = await response.text();
      result = JSON.parse(responseText);
    } catch (parseError) {
      result = { success: true, message: 'Response received but not JSON' };
    }

    return {
      success: true,
      message: 'Package purchase notification sent successfully',
      data: result,
      timestamp: new Date().toISOString(),
      sentTo: formattedPhone,
      userName: userName,
      packageName: packageName
    };

  } catch (error) {
    // Don't fail the purchase process if WhatsApp fails
    return {
      success: false,
      error: error.message || 'Failed to send WhatsApp notification',
      errorType: error.name || 'Unknown',
      timestamp: new Date().toISOString(),
      phoneNumber: phoneNumber || 'not provided',
      userName: userName || 'not provided'
    };
  }
};

/**
 * Format experience for WhatsApp message
 * Converts experience array to a readable string
 * @param {Array} experienceArray - Array of experience objects
 * @returns {string} Formatted experience string
 */
export const formatExperienceForWhatsApp = (experienceArray) => {
  if (!experienceArray || !Array.isArray(experienceArray) || experienceArray.length === 0) {
    return 'Fresher';
  }

  // Calculate total years of experience
  let totalYears = 0;

  experienceArray.forEach(exp => {
    if (exp.startDate) {
      const start = new Date(exp.startDate);
      const end = exp.current ? new Date() : (exp.endDate ? new Date(exp.endDate) : new Date());
      const years = (end - start) / (1000 * 60 * 60 * 24 * 365);
      totalYears += years;
    }
  });

  if (totalYears === 0) {
    return 'Fresher';
  }

  const years = Math.floor(totalYears);
  const months = Math.round((totalYears - years) * 12);

  if (years === 0) {
    return `${months} months`;
  } else if (months === 0) {
    return `${years} years`;
  } else {
    return `${years}.${months} years`;
  }
};

/**
 * Format education/qualification for WhatsApp message
 * Converts education array to highest qualification
 * @param {Array} educationArray - Array of education objects
 * @returns {string} Formatted qualification string
 */
export const formatQualificationForWhatsApp = (educationArray) => {
  if (!educationArray || !Array.isArray(educationArray) || educationArray.length === 0) {
    return 'Not specified';
  }

  // Define education level hierarchy
  const educationHierarchy = {
    'phd': 6,
    'doctorate': 6,
    'doctor': 6,
    'masters': 5,
    'master': 5,
    'mba': 5,
    'mca': 5,
    'm.tech': 5,
    'postgraduate': 5,
    'pg': 5,
    'bachelor': 4,
    'b.tech': 4,
    'bca': 4,
    'bba': 4,
    'undergraduate': 4,
    'ug': 4,
    'diploma': 3,
    'certificate': 2,
    'high school': 1,
    '12th': 1,
    '10th': 1,
    'secondary': 1
  };

  let highestEducation = educationArray[0];
  let highestLevel = 0;

  educationArray.forEach(edu => {
    const degree = (edu.degree || '').toLowerCase();

    // Find matching level
    for (const [key, level] of Object.entries(educationHierarchy)) {
      if (degree.includes(key) && level > highestLevel) {
        highestLevel = level;
        highestEducation = edu;
      }
    }
  });

  return highestEducation.degree || 'Not specified';
};

/**
 * Format location for WhatsApp message
 * @param {string|Object} location - Location string or object
 * @returns {string} Formatted location string
 */
export const formatLocationForWhatsApp = (location) => {
  if (!location) {
    return 'Not specified';
  }

  if (typeof location === 'string') {
    return location;
  }

  if (typeof location === 'object') {
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.state) parts.push(location.state);
    if (location.country) parts.push(location.country);
    return parts.join(', ') || 'Not specified';
  }

  return 'Not specified';
};

/**
 * Test the WhatsApp service with dummy data
 * @returns {Promise<Object>} Test result
 */
export const testWhatsAppService = async () => {
  console.log('🧪 [WhatsApp] Running service test...');

  const dummyJobData = {
    title: 'Software Engineer',
    company: 'Tech Corp',
    location: { city: 'Mumbai', state: 'Maharashtra' },
    jobType: 'Full-time',
    experienceLevel: 'Mid-level',
    workMode: 'Remote',
    description: 'Exciting opportunity for a software engineer with experience in JavaScript and Node.js.'
  };

  const dummyUserInfo = {
    name: 'John Doe',
    phone: '917058869790'
  };

  const dummyPackageInfo = {
    remainingJobs: 5,
    totalJobs: 10,
    usedJobs: 4
  };

  return await sendJobPostingNotification(dummyJobData, dummyUserInfo, dummyPackageInfo);
};

/**
 * Get WhatsApp service configuration
 * @returns {Object} Configuration object
 */
export const getWhatsAppConfig = () => {
  return { ...WHATSAPP_CONFIG };
};

/**
 * Update WhatsApp service configuration
 * @param {Object} newConfig - New configuration values
 */
export const updateWhatsAppConfig = (newConfig) => {
  Object.assign(WHATSAPP_CONFIG, newConfig);
  console.log('⚙️ [WhatsApp] Configuration updated:', WHATSAPP_CONFIG);
};

const whatsappService = {
  sendJobPostingNotification,
  sendJobApplicationNotification,
  sendPackagePurchaseNotification,
  formatExperienceForWhatsApp,
  formatQualificationForWhatsApp,
  formatLocationForWhatsApp,
  testWhatsAppService,
  getWhatsAppConfig,
  updateWhatsAppConfig
};

export default whatsappService;

